import { z } from 'zod';

/**
 * Type guard that checks if a value is a valid member of an enum.
 *
 * Uses Zod's `z.nativeEnum()` internally for validation while providing
 * a clean type guard API with proper TypeScript type narrowing.
 *
 * @param value - The value to check against the enum.
 * @param enumObj - The enum object to validate against.
 * @returns `true` if the value is a valid enum member, with type narrowing.
 *
 * @example
 * enum Status { Active = 'active', Inactive = 'inactive' }
 *
 * const value: string = 'active';
 * if (isEnumValue(value, Status)) {
 *   // value is now typed as Status
 *   console.log(value); // TypeScript knows this is Status
 * }
 *
 * @example
 * // Use in validation logic
 * if (!isEnumValue(userInput, GroupName)) {
 *   throw new Error(`Invalid group: ${userInput}`);
 * }
 * // userInput is now typed as GroupName after this check
 */
export function isEnumValue<T extends { [key: string]: string | number }>(
  value: unknown,
  enumObj: T
): value is T[keyof T] {
  // Zod's EnumLike type has stricter index signature requirements than our
  // generic constraint; the `any` cast is safe because runtime validation is
  // handled by Zod and type safety is provided by the return type guard
  //
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- See above.
  const schema = z.nativeEnum(enumObj as any);
  return schema.safeParse(value).success;
}
